import React, { useEffect } from "react";
import { Container, Card, Image, Table } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatMomentDate } from "../../../utils/dateHelper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "./index.css"
import { fetchListingDetails, clearListingDetails, moderateListing } from "../../../redux/slice/marketplaceSlice";
import { useLoader } from "../../../context/LoaderContext";
import { usePermission } from "../../../hooks/usePermission";
import { routes } from "../../../routes/pageRoutes";
import { ArrowLeftIcon, EditIcon, DeleteIcon, PdfIcon, DownloadIcon, ArrowRightIcon, ClockIcon, FullClockIcon } from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import { goBack } from "../../../utils/otherHelopers";
import { createPortal } from "react-dom";
import StatusChip from "../../../components/statusChip/StatusChip";
import { ProfileCard } from "../../../components/custom-card/Cards";

const MODULE_SLUG = "marketplace_bulk_offers";
const renderHeaderContent = (onBack, status) => (
    <div className="d-flex align-items-center gap-3 w-100">
        <div className="d-flex align-items-center gap-2">
            <button
                onClick={onBack}
                type="button"
                className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
            >
                <ArrowLeftIcon size={22} strokeWidth="2.2" />
            </button>
            <h4 className="fw-semibold mb-0" style={{ fontSize: '18px' }}>
                Bulk Offer Details
            </h4>
        </div>
        {status && (
            <StatusChip
                title={status}
                pill
                bg={status === "approved" ? "success" : status === "rejected" ? "danger" : "warning"}
                className="text-capitalize"
            />
        )}
    </div>
);
const ViewBulkOffer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { setLoading } = useLoader();
    const location = useLocation();


    const { details, isLoading } = useSelector((state) => state.marketplace);
    const { hasPermission } = usePermission();

    const canUpdate = hasPermission(MODULE_SLUG, "update");
    const canDelete = hasPermission(MODULE_SLUG, "delete");

    useEffect(() => {
        if (id) {
            setLoading(true);
            dispatch(fetchListingDetails({ id, seller_type: 'company' })).finally(() => setLoading(false));
        }
        return () => {
            dispatch(clearListingDetails());
        };
    }, [id, dispatch, setLoading]);

    const handlePublish = async () => {
        try {
            await dispatch(moderateListing({ id, status: 'published', seller_type: 'company' })).unwrap();
            dispatch(fetchListingDetails({ id, seller_type: 'company' }));
        } catch (error) {
            console.error("Publish error :::", error);
        }
    };

    if (isLoading) {
        return null;
    }

    if (!details) {
        return <div className="p-5 text-center">Offer not found</div>;
    }

    const offerDetails = details?.company_offer_details || {};
    const productDetails = details?.company_product_details || {};
    const company = details?.company || {};
    const sellerInfo = details?.company_seller_info || {};

    const discount = offerDetails.mrp && offerDetails.offer_price
        ? Math.round(((offerDetails.mrp - offerDetails.offer_price) / offerDetails.mrp) * 100)
        : 0;

    const formatCurrency = (val) => {
        const num = Number(val) || 0;
        return num % 1 === 0 ? num.toString() : num.toFixed(2);
    };

    const stats = [
        { label: "MRP", value: `₹${formatCurrency(offerDetails.mrp)}/-` },
        { label: "Offer Price", value: `₹${formatCurrency(offerDetails.offer_price)}/-`, color: 'secondary' },
        { label: "Total Stock", value: `${offerDetails.available_qty || 0} Unit` },
        { label: "1 Lot", value: `${offerDetails.lot_size || 0} Unit` },
    ];
    const headerPortalTarget = document.getElementById("header-portal-target");
    const handleBack = () => {
        goBack(navigate, location, routes.marketplaceListings);
    };
    return (<>
        {headerPortalTarget && createPortal(renderHeaderContent(handleBack, details?.status), headerPortalTarget)}
        <Container fluid className="px-0 pb-5 bulk-detail-view">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">

                <div className="d-flex align-items-center gap-2">
                    {details?.status === 'approved' && canUpdate && (
                        <CommonButton
                            title="Publish"
                            variant="primary"
                            className="p-2"
                            onClick={handlePublish}
                        />
                    )}
                    {details?.status !== 'published' && (
                        <>
                            {canUpdate && (
                                <CommonButton variant="outline-primary"
                                    className="p-2"
                                    onClick={() => navigate(routes.editMarketplaceBulkOffer.replace(":id", id))}
                                    icon={<EditIcon size={18} />}
                                />
                            )}
                            {canDelete && (
                                <CommonButton variant="outline-danger"
                                    className="p-2"
                                    onClick={() => navigate(routes.editMarketplaceBulkOffer.replace(":id", id))}
                                    icon={<DeleteIcon size={18} />}
                                />
                            )}
                        </>
                    )}

                </div>
            </div>

            <div className="grid columns gap-4 align-items-start">
                {/* Main Main Content Card */}
                <Card className="rounded-2 p-3 border-0 common-shadow g-col-8 grid">
                    <div className="g-col-5">
                        <div className="swiper-nav-wrapper position-relative" >
                            {details?.photos?.length > 1 ? (<>
                                <Swiper
                                    modules={[Navigation, Pagination, Autoplay]}

                                    pagination={{ clickable: true }}
                                    loop={details?.photos?.length > 1}
                                    navigation={{
                                        nextEl: `.custom-next`,
                                        prevEl: `.custom-prev`
                                    }}
                                    autoplay={{ delay: 3000, disableOnInteraction: true }}
                                // className="h-100"
                                >
                                    {details.photos.map((img, idx) => (
                                        <SwiperSlide key={idx} className="rounded-2">
                                            <div className="rounded-2 overflow-hidden" style={{ height: '350px' }} >
                                                <Image src={img.url} className="w-100 h-auto object-fit-cover" style={{ objectPosition: "center" }} />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <button className={`custom-prev swiper-nav-btn prev-btn text-primary`} >
                                    <ArrowLeftIcon size={12} />
                                </button>
                                <button className={`custom-next swiper-nav-btn next-btn text-primary`} >
                                    <ArrowRightIcon size={12} />
                                </button></>
                            ) : details?.photos?.length === 1 ? (<div className="rounded-2 overflow-hidden" style={{ height: '350px' }} >
                                <Image src={details?.photos[0]?.url} className="w-auto h-100 object-fit-cover" style={{ objectPosition: "center" }} />
                            </div>) : (
                                <div className="h-100 bg-light d-flex align-items-center justify-content-center text-muted">No Image Available</div>
                            )}

                        </div>
                    </div>

                    <div className="ps-md-2 mt-3 mt-md-0 g-col-7">
                        <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                            <h5 className="fw-normal mb-0">{details?.name}</h5>
                            {discount > 0 && (
                                <StatusChip title={`Save ${discount}% Off`} bg="secondary" />
                            )}
                        </div>
                        <p className="text-secondary mb-2 pb-1 lh-base">{details?.brief_description || details?.description}</p>

                        <div className="d-flex flex-wrap gap-5 pb-1">
                            {stats.map((s, i) => (
                                <div key={i} className="text-center">
                                    <h5 className={`fw-semibold h6 mb-1 ${s.color}`}>{s.value}</h5>
                                    <span className="text-secondary fs-sm" >{s.label}</span>
                                </div>
                            ))}
                        </div>
                        <hr className="border-top opacity-100 my-2" />
                        <div className="d-flex flex-wrap gap-4 py-1">
                            <div className="d-flex align-items-center gap-2">
                                <i className="p-2 rounded-2 primary lh-1 border"><EditIcon size={18} /></i>
                                <div>
                                    <p className="text-secondary fs-xs text-uppercase m-0">Creation Date</p>
                                    <span className="m-0 fs-sm">{formatMomentDate(details?.created_at, "DD MMMM, YYYY")}</span>
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <i className="p-2 rounded-2 primary lh-1 border">
                                    <FullClockIcon size={18} />
                                </i>
                                <div>
                                    <p className="text-secondary fs-xs text-uppercase m-0">Offer Start Date</p>
                                    <span className="m-0 fs-sm">{offerDetails.start_date ? formatMomentDate(offerDetails.start_date, "DD MMMM, YYYY") : "-"}</span>
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <i className="p-2 rounded-2 primary lh-1 border">
                                    <ClockIcon size={18} />
                                </i>
                                <div>
                                    <p className="text-secondary fs-xs text-uppercase m-0">Offer End Date</p>
                                    <span className="m-0 fs-sm">{offerDetails.end_date ? formatMomentDate(offerDetails.end_date, "DD MMMM, YYYY") : "-"}</span>
                                </div>
                            </div>
                        </div>
                        <hr className="border-top opacity-100 my-2" />
                        <ul className="pt-1 mb-0 ps-3">
                            {(productDetails.features || details?.description || "").split('\n').filter(f => f.trim()).map((f, i) => (
                                <li key={i} className="fs-sm text-secondary">
                                    {f.replace(/^[•\-*]\s*/, '')}
                                </li>
                            ))}
                        </ul>
                    </div>
                </Card>
                <ProfileCard title="Company Details" link={routes.viewCompany.replace(":id", company.id)} className="g-col-4" user={{ website: company?.profile?.website, email: company?.profile?.email, mobile_no: company?.profile?.mobile_number, date: company.created_at, image: company?.profile?.logo?.url, name: company.name, role: company.category?.name || "Construction", }} />
                <div className="g-col-6">
                    <Card className="rounded-2 p-3 border-0 common-shadow mb-4">
                        <h5 className="fw-normal fs-lg pb-1">Product Detail</h5>
                        <hr className="border-top opacity-100 mt-2 mb-0" />
                        <Table className="fs-md">
                            <tbody>
                                {[
                                    { label: "Product SKU", value: productDetails.sku },
                                    { label: "Quantity", value: productDetails.variations },
                                    { label: "Lot Size", value: `${offerDetails.lot_size} Unit` },
                                    { label: "Warranty", value: productDetails.warranty },
                                    { label: "Product Features", value: productDetails.features ? "High Strength, Quick Setting" : "-" }, // Example mapping
                                ].map((row, i) => (
                                    <tr key={i}>
                                        <td className="text-secondary w-50">{row.label}</td>
                                        <td className="text-primary fw-semibold">{row.value || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>

                    <Card className="rounded-2 p-3 mb-4 border-0 common-shadow">
                        <h5 className="fw-normal fs-lg pb-1">Product Specification</h5>
                        <hr className="border-top opacity-100 my-2" />
                        <p className="text-secondary fs-md m-0 pt-1">{productDetails.specifications}</p>

                    </Card>
                    <Card className="rounded-2 p-3 border-0 common-shadow">
                        <h5 className="fw-normal fs-lg pb-1">Terms & Conditions</h5>
                        <hr className="border-top opacity-100 my-2" />
                        <p className="text-secondary fs-md m-0 pt-1">{productDetails.terms}</p>
                    </Card>
                </div>
                <div className="g-col-6">



                    <Card className="rounded-2 p-3 border-0 common-shadow mb-4">
                        <h5 className="fw-normal fs-lg pb-1">Top Highlights</h5>
                        <hr className="border-top opacity-100 mt-2 mb-0" />
                        <Table className="fs-md">
                            <tbody>
                                {[
                                    { label: "Company Type", value: sellerInfo.company_type },
                                    { label: "Manufacturer Name", value: sellerInfo.manufactured_by },
                                    { label: "Manufacturer Address", value: sellerInfo.plant_address },
                                    { label: "Marketed By", value: sellerInfo.marketed_by },
                                    {
                                        label: "Serviceability",
                                        value: offerDetails.serviceability_type === 'pan_india'
                                            ? "Pan India"
                                            : details?.serviceable_cities?.length > 0
                                                ? details.serviceable_cities.map(c => c.name).join(", ")
                                                : "-"
                                    },
                                    { label: "Country Of Origin", value: productDetails.country?.name || "India" },
                                ].map((row, i) => (
                                    <tr key={i}>
                                        <td className="text-secondary w-50">{row.label}</td>
                                        <td className="text-primary fw-semibold">{row.value || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>

                    <Card className="rounded-2 p-3 border-0 common-shadow">
                        <h5 className="fw-normal fs-lg pb-1">Brochure Document</h5>
                        <hr className="border-top opacity-100 my-2" />
                        {details?.document ? (
                            <div className="px-3 py-2 bg-light rounded-2 d-flex align-items-center justify-content-between ">
                                <div className="d-flex align-items-center gap-3 fs-md">
                                    <PdfIcon size={24} className="primary" />
                                    <div className="overflow-hidden" style={{ maxWidth: '150px' }}>
                                        <p className="fw-medium mb-0 text-truncate lh-1">{details.document.name || "Brochure.pdf"}</p>
                                        <small className="text-secondary lh-1">{(details.document.size / 1024 / 1024).toFixed(2)} MB</small>
                                    </div>
                                </div>
                                <a href={details.document.url} target="_blank" rel="noreferrer" className="text-secondary">
                                    <DownloadIcon size={24} />
                                </a>
                            </div>
                        ) : (
                            <div className="text-center text-muted py-3 smallest">No brochure available</div>
                        )}
                    </Card>
                </div>

            </div>
        </Container >

    </>

    );
};

export default ViewBulkOffer;
